import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';

interface ConditionBlock {
  field: string;
  operator: string;
  value: string;
}

const ScratchConditionNode: React.FC<NodeProps> = ({ data, selected }) => {
  const config = data.config || {};
  const colors = getNodeColors('multi_condition');
  
  // Feltételek és operátor (AND/OR)
  const conditions: ConditionBlock[] = config.conditions || [];
  const logicOperator = config.operator || 'AND';

  return (
    <div className={`
      px-4 py-3 bg-gradient-to-r ${colors.gradient.from} ${colors.gradient.to}
      border-2 transition-all duration-200 ${
        selected ? `${colors.border.selected} shadow-xl scale-105` : `${colors.border.default} shadow-lg`
      }
      rounded-lg min-w-[280px] max-w-[400px]
    `}>
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 bg-white border-2 ${colors.handle}`}
        style={{ left: -6 }}
      />
      
      {/* Header */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <PuzzlePieceIcon className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-semibold ${colors.text.primary} truncate`}>
              Összetett Feltétel
            </h3>
            {/* Logic Operator Badge */}
            <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
              logicOperator === 'AND' 
                ? 'bg-green-500 bg-opacity-30 text-green-100' 
                : 'bg-blue-500 bg-opacity-30 text-blue-100'
            }`}>
              {logicOperator}
            </span>
          </div>
          <p className={`text-xs ${colors.text.secondary}`}>
            {conditions.length} feltétel
          </p>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Scratch-style Condition Blocks */}
      <div className="space-y-1.5">
        {conditions.length > 0 ? (
          conditions.map((condition, index) => (
            <div key={index} className="relative">
              {/* Scratch-style puzzle piece block */}
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-md p-2 border border-white border-opacity-20">
                <div className="flex items-center gap-2 text-xs">
                  {/* Field */}
                  <div className="px-2 py-1 bg-purple-600 bg-opacity-40 rounded text-white font-medium truncate max-w-[80px]">
                    {condition.field || 'field'}
                  </div>
                  
                  {/* Operator */}
                  <div className="px-2 py-1 bg-indigo-600 bg-opacity-40 rounded text-white font-mono">
                    {condition.operator || '=='}
                  </div>
                  
                  {/* Value */}
                  <div className="flex-1 px-2 py-1 bg-pink-600 bg-opacity-40 rounded text-white truncate">
                    {condition.value || 'value'}
                  </div>
                </div>
              </div>
              
              {/* Connector line between blocks (except last) */}
              {index < conditions.length - 1 && (
                <div className="flex items-center justify-center py-0.5">
                  <div className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                    logicOperator === 'AND' 
                      ? 'bg-green-500 bg-opacity-20 text-green-200' 
                      : 'bg-blue-500 bg-opacity-20 text-blue-200'
                  }`}>
                    {logicOperator}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-xs text-center py-2 text-white text-opacity-60">
            Nincs feltétel beállítva
          </div>
        )}
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData !== undefined && (
        <div className={`mt-2 text-xs px-2 py-1 rounded font-semibold ${
          data.executionData 
            ? 'bg-green-500 bg-opacity-20 text-green-200' 
            : 'bg-red-500 bg-opacity-20 text-red-200'
        }`}>
          Eredmény: {data.executionData ? '✓ IGAZ' : '✗ HAMIS'}
        </div>
      )}

      {/* Output handles - TRUE és FALSE ágak */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        className="w-3 h-3 bg-green-400 border-2 border-green-600"
        style={{ right: -6, top: '40%' }}
      />
      
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        className="w-3 h-3 bg-red-400 border-2 border-red-600"
        style={{ right: -6, top: '60%' }}
      />
      
      {/* Labels for outputs */}
      <div 
        className="absolute -right-5 text-xs font-bold text-green-300 pointer-events-none select-none"
        style={{ top: 'calc(40% - 8px)' }}
        title="IGAZ"
      >
        ✓
      </div>
      <div 
        className="absolute -right-5 text-xs font-bold text-red-300 pointer-events-none select-none"
        style={{ top: 'calc(60% - 8px)' }}
        title="HAMIS"
      >
        ✗
      </div>
    </div>
  );
};

export default ScratchConditionNode;
